import { Button } from '@codegouvfr/react-dsfr/Button';
import { useTextes } from '@common/config';
import { EditOfaInfoStep2FormResult, editOfaInfoStep2FormSchema } from '@common/schemas/forms/organisme';
import { CampagneDto, OrganismeDetailsDto } from '@common/services';
import { DateField, Form, FormField, YesNoRadioGroupField } from '@common/ui';
import { areSomePropertiesDifferent, DateUtils } from '@common/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ZodObject } from 'zod';
import { useAppDispatch } from '../../../../hooks/store.hook';
import { needNewDeclaration } from '../../../Dashboard/components/VerticalStepper/stores/dashboard-stepper.slice';

const COMPARED_FIELDS: (keyof EditOfaInfoStep2FormResult)[] = [
  'avaitActiviteApprentissage', 'horsLesMurs', 'dateDebutActivite', 'dateDebutExerciceComptable',
  'dateFinExerciceComptable', 'partActiviteApprentissage', 'hasGroupe', 'groupe', 'hasReseau', 'reseau',
  'cfa', 'sousTraitant',
];

export const EditOfaInfoStep2 = ({ organisme, organismePrecedent, campagne, step1HasChanged, handleSubmit, clickPrecedent }: EditOfaInfoStep2Props) => {
  const t = useTextes('ParcoursOfa', 'EtapierStep1');
  const dispatch = useAppDispatch();

  const formSchemaRef = useRef<ZodObject>(editOfaInfoStep2FormSchema);

  const [openGroupe, setOpenGroupe] = useState<boolean>(false);
  const [openReseau, setOpenReseau] = useState<boolean>(false);

  const resolveField = (current: string | null | undefined, previous: string | null | undefined): string =>
    current?.trim() ? String(current) : (previous ?? '');

  const resolveBoolean = (current: boolean | null | undefined, previous: boolean | null | undefined): 'true' | 'false' =>
    current != null ? (current ? 'true' : 'false') : (previous ? 'true' : 'false');

  const values = useMemo<EditOfaInfoStep2FormResult>(() => ({
    avaitActiviteApprentissage: resolveBoolean(organisme.avaitActiviteApprentissage, organismePrecedent?.avaitActiviteApprentissage),
    horsLesMurs: resolveBoolean(organisme.horsLesMurs, organismePrecedent?.horsLesMurs),
    dateDebutActivite: resolveField(DateUtils.formatForInput(organisme.dateDebutActivite ?? ''), DateUtils.formatForInput(organismePrecedent?.dateDebutActivite ?? '')),
    dateDebutExerciceComptable: resolveField(DateUtils.formatForInput(organisme.dateDebutExerciceComptable ?? ''), DateUtils.formatForInput(organismePrecedent?.dateDebutExerciceComptable ?? '')),
    dateFinExerciceComptable: resolveField(DateUtils.formatForInput(organisme.dateFinExerciceComptable ?? ''), DateUtils.formatForInput(organismePrecedent?.dateFinExerciceComptable ?? '')),
    partActiviteApprentissage: organisme.partActiviteApprentissage ?? organismePrecedent?.partActiviteApprentissage,
    hasGroupe: resolveBoolean(!!organisme.groupe || null, !!organismePrecedent?.groupe || null),
    groupe: resolveField(organisme.groupe, organismePrecedent?.groupe),
    hasReseau: resolveBoolean(!!organisme.reseau || null, !!organismePrecedent?.reseau || null),
    reseau: resolveField(organisme.reseau, organismePrecedent?.reseau),
    cfa: resolveBoolean(organisme.cfa, organismePrecedent?.cfa),
    sousTraitant: resolveBoolean(organisme.sousTraitant, organismePrecedent?.sousTraitant),
  }), [organisme, organismePrecedent]);

  useEffect(() => {
    if (organisme.groupe || organismePrecedent?.groupe) setOpenGroupe(true);
    if (organisme.reseau || organismePrecedent?.reseau) setOpenReseau(true);
  }, [organisme, organismePrecedent]);

  const handleSubmitStep2 = (editOfaInfoStep2FormResult: EditOfaInfoStep2FormResult) => {
    const normalizedValues = { ...values, partActiviteApprentissage: values.partActiviteApprentissage ?? undefined };
    const normalizedResult = { ...editOfaInfoStep2FormResult, partActiviteApprentissage: editOfaInfoStep2FormResult.partActiviteApprentissage ?? undefined };

    const step2HasChanged = areSomePropertiesDifferent(normalizedValues, normalizedResult, COMPARED_FIELDS);

    if (step1HasChanged || step2HasChanged) {
      dispatch(needNewDeclaration());
    }

    handleSubmit(editOfaInfoStep2FormResult);
  };

  return (
    <div className={'flex flex-col p-6'}>
      <Form<EditOfaInfoStep2FormResult> schema={formSchemaRef.current}
                                        defaultValues={values}
                                        className={'flex flex-col gap-6'}
                                        onSubmit={handleSubmitStep2}>

        <YesNoRadioGroupField name="avaitActiviteApprentissage"
                              title={`Votre organisme a-t-il eu une activité apprentissage en ${campagne?.annee - 1} ?`} />

        <YesNoRadioGroupField name="horsLesMurs"
                              title={`L'organisme déclarant en ${campagne?.annee - 2} est-il un organisme dit "CFA hors-les-murs" ?`}
                              tooltip={t['DONNEE_OFA_COMP_HORS_LES_MURS_INFO_BULLE_MSG']} />

        <DateField name="dateDebutActivite"
                   title="Date de début d'activité apprentissage de votre organisme"
                   placeholder={'25/02/2022'} />

        <DateField name="dateDebutExerciceComptable"
                   title="Date de début de l'exercice comptable de l'organisme déclarant"
                   placeholder={'25/02/2022'} />

        <DateField name="dateFinExerciceComptable"
                   title="Date de fin de l'exercice comptable de l'organisme déclarant"
                   placeholder={'25/02/2022'} />

        <FormField name="partActiviteApprentissage"
                   title="Part de l'activité apprentissage dans l'activité totale de votre organisme (%)"
                   placeholder={'100'}
                   tooltip={t['DONNEE_OFA_COMP_APPRENTISSAGE_PART_MSG']} />

        <YesNoRadioGroupField
          name="hasGroupe"
          title="L'organisme appartient-il à un groupe ?"
          onChange={(value) => setOpenGroupe((value === 'true'))}
        />

        {openGroupe && (
          <FormField
            name="groupe"
            title="Lequel ?"
            placeholder="Galiléo"
            forceRequiredIndicator />
        )}

        <YesNoRadioGroupField
          name="hasReseau"
          title="L'organisme appartient-il à un réseau ?"
          onChange={(value) => setOpenReseau((value === 'true'))}
        />

        {openReseau && (
          <FormField
            name="reseau"
            title="Lequel ?"
            placeholder="Galiléo"
            forceRequiredIndicator />
        )}

        <YesNoRadioGroupField
          name="cfa"
          title="L’organisme est-il un CFA d’entreprise (au sens de la loi du 5 septembre 2018) ?"
        />

        <YesNoRadioGroupField
          name="sousTraitant"
          title={`L’organisme intervient-il également en tant que "sous-traitant", au sens délégation de l'action de formation, pour le compte d'un autre organisme au titre de l'apprentissage ?`}
        />

        <div className={'flex justify-end gap-2'}>
          <Button type={'button'} priority={'secondary'}
                  onClick={() => clickPrecedent()}>PRECEDENT</Button>
          <Button type={'submit'}>ENREGISTRER</Button>
        </div>
      </Form>
    </div>
  );
};

interface EditOfaInfoStep2Props {
  organisme: OrganismeDetailsDto,
  organismePrecedent: OrganismeDetailsDto | null;
  campagne: CampagneDto | undefined;
  step1HasChanged: boolean;
  handleSubmit: (edit: EditOfaInfoStep2FormResult) => void,
  clickPrecedent: () => void
}